import * as t from 'io-ts';

import {colorForTeammateId} from '../../../libs/shared/ui/src/styles/colorHelpers';
import {PaletteColorsEnum} from '../../../libs/shared/ui/src/styles/paletteModel';
import {
  makeApiSerializableType,
  makeApiType,
  makeEnumRuntimeType,
  RContactUidRef,
  RLazyTeammateIdRef,
  RTagIdRef,
} from './apiTypes';
import {buildContactUid, ContactSourcesEnum} from './contactModel';
import {parseCustomFieldAttribute, RCustomFieldAttributes} from './customFieldAttributeModel';
import {parseTeammateNotificationRules, RTeammateNotificationRules} from './notificationRulesModel';
import {parseTeammateAccess, RTeammateAccess} from './teammateAccessModel';

/*
 * Runtime type.
 */

export enum TeammateAccountStatusesEnum {
  ACTIVE = 'active',
  PENDING = 'pending',
  PENDING_EMAIL = 'pending_email',
  BLOCKED = 'blocked',
  DELETED = 'deleted',
}

export enum TeammateLicenseTypesEnum {
  FULL = 'full',
  LIGHT = 'light',
  VISITOR = 'visitor',
  TEMPLATE = 'archetype',
  BOT = 'bot',
}

export enum TeammateFlagsEnum {
  IS_ONBOARDING_DEMO = 'is_onboarding_demo',
  IS_UNVERIFIED_DOMAIN_LOGIN = 'is_unverified_domain_login',
  IS_GOOGLE_SIGNUP = 'is_google_signup',
  IS_OFFICE365_SIGNUP = 'is_office_365_signup',
}

const RTeammateBase = t.type({
  id: t.Integer,
  url: t.string,
  namespace: t.string,
  email: t.string,
  contactUid: RContactUidRef,
  alias: t.string,
  givenName: t.string,
  familyName: t.string,
  phone: t.string,
  isAvailable: t.boolean,
  color: makeEnumRuntimeType(PaletteColorsEnum),
  accountStatus: makeEnumRuntimeType(TeammateAccountStatusesEnum),
  licenseType: makeEnumRuntimeType(TeammateLicenseTypesEnum),
  access: RTeammateAccess,
  avatarUrl: t.union([t.undefined, t.string]),
  inboxTagId: RTagIdRef,
  starredTagId: RTagIdRef,
  isGuidedDemo: t.boolean,
  isUnverified: t.boolean,
  isGoogleSignup: t.boolean,
  isOffice365Signup: t.boolean,
  createdAt: t.Integer,
  updatedAt: t.Integer,
  is2FAEnabled: t.boolean,
  customFieldAttributes: t.union([t.undefined, RCustomFieldAttributes]),
  notificationRules: RTeammateNotificationRules,
});

const RTeammateDirty = t.readonly(RTeammateBase, 'teammate');
const RLazyTeammateDirty = t.readonly(RTeammateBase, 'lazyTeammate');
const RTeammatesSearch = t.readonly(
  t.type({
    items: t.readonlyArray(RTeammateDirty),
    limit: t.Integer,
    total: t.Integer,
  }),
);

const RTeammates = t.readonlyArray(RTeammateDirty);

/*
 * Type.
 */

export interface TeammateModel extends t.TypeOf<typeof RTeammateDirty> {}
export const RTeammate = t.clean<TeammateModel>(RTeammateDirty as any);
export const RLazyTeammate = t.clean<TeammateModel>(RLazyTeammateDirty as any);
RLazyTeammateIdRef.setTargetType(RLazyTeammate);
export interface TeammatesSearch extends t.TypeOf<typeof RTeammatesSearch> {}

/*
 * IO methods.
 */

export function parseTeammate(src: any): TeammateModel {
  return {
    id: src.id,
    url: src.url,
    namespace: src.namespace,
    email: src.email,
    contactUid: buildContactUid(ContactSourcesEnum.EMAIL, src.email),
    alias: src.alias,
    givenName: src.given_name,
    familyName: src.family_name,
    phone: src.phone || '',
    avatarUrl: (src.has_custom_avatar && src.avatar) || undefined,
    isAvailable: src.available,
    color: colorForTeammateId(src.id),
    accountStatus: src.account_status,
    licenseType: src.license_type || TeammateLicenseTypesEnum.FULL, // TODO: Remove default value.
    access: parseTeammateAccess(src.access),
    inboxTagId: src.inbox_tag_id || 0,
    starredTagId: src.starred_tag_id || 0,
    isGuidedDemo: Boolean(
      Array.isArray(src.extra) && src.extra.includes(TeammateFlagsEnum.IS_ONBOARDING_DEMO),
    ),
    isUnverified: Boolean(
      Array.isArray(src.extra) && src.extra.includes(TeammateFlagsEnum.IS_UNVERIFIED_DOMAIN_LOGIN),
    ),
    isGoogleSignup: Boolean(
      Array.isArray(src.extra) && src.extra.includes(TeammateFlagsEnum.IS_GOOGLE_SIGNUP),
    ),
    isOffice365Signup: Boolean(
      Array.isArray(src.extra) && src.extra.includes(TeammateFlagsEnum.IS_OFFICE365_SIGNUP),
    ),
    createdAt: src.created_at,
    updatedAt: src.updated_at,
    is2FAEnabled: src.two_factor_auth_status === 'enabled',
    customFieldAttributes:
      (Array.isArray(src.custom_field_attributes) &&
        src.custom_field_attributes.map(parseCustomFieldAttribute)) ||
      undefined,
    notificationRules: parseTeammateNotificationRules(src.notification_rules),
  };
}

export function serializeTeammate(teammate: TeammateModel): any {
  return {
    id: teammate.id,
    namespace: teammate.namespace,
    email: teammate.email,
    alias: teammate.alias,
    given_name: teammate.givenName,
    family_name: teammate.familyName,
    phone: teammate.phone,
    avatar: teammate.avatarUrl,
    available: teammate.isAvailable,
    account_status: teammate.accountStatus,
    license_type: teammate.licenseType,
  };
}

function parseTeammatesSearch(src: any): TeammatesSearch {
  return {
    items: (src.teammates ?? []).map(parseTeammate),
    limit: src.limit,
    total: src.total,
  };
}

function parseTeammates(src: any): ReadonlyArray<TeammateModel> {
  return (src.team ?? []).map(parseTeammate);
}

/*
 * API type.
 */

export const ApiTeammate = makeApiSerializableType('teammate', RTeammate, parseTeammate, serializeTeammate);
export const ApiTeammates = makeApiType('teammates', RTeammates, parseTeammates);
export const ApiTeammatesSearch = makeApiType('teammatesSearch', RTeammatesSearch, parseTeammatesSearch);
